import { FC, useCallback, useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Add } from '@mui/icons-material'
import { Button, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InputTranslatorType } from '../../../../types/InputTranslatorType'
import { categoryLanguages } from '../../helpers/categoryLanguageHelpers'
import { FeatureExample } from '../../types/Categories'
import { ExampleItemDialog, exampleEditorConfig } from '../ExampleItemDialog/ExampleItemDialog'
import { ExampleItemsTable } from '../ExampleItemsTable/ExampleItemsTable'
import { ExampleFormValues, ExampleItemValues, ExampleList, newExampleItemValues, toExampleFormValues, toExampleSaveBody } from '../helpers/featureHelpers'

const lists: ExampleList[] = ['corrects', 'wrongs']
const listLabelKeys: Record<ExampleList, string> = {
  corrects: 'internal-analysts:categories_example_corrects',
  wrongs: 'internal-analysts:categories_example_wrongs',
}

// The backdrop passes its click event as the confirmation, so a confirmation is recognised by its data.
const removeAction = 'remove'

type ItemEdit = { list: ExampleList; index?: number; item: ExampleItemValues }

type ExampleEditDialogProps = {
  open: boolean
  positionName: string
  example?: FeatureExample
  onClose: () => void
  onDone: (example: FeatureExample) => void
}

const emptyValues: ExampleFormValues = { descriptions: {}, corrects: [], wrongs: [] }

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const ExampleEditDialog: FC<ExampleEditDialogProps> = ({ open, positionName, example, onClose, onDone }) => {
  const { t } = useTranslation()
  const { control, reset, setValue, getValues } = useForm<ExampleFormValues>({ defaultValues: example ? toExampleFormValues(example) : emptyValues })
  const corrects = useWatch({ control, name: 'corrects' })
  const wrongs = useWatch({ control, name: 'wrongs' })
  const [itemEdit, setItemEdit] = useState<ItemEdit>()
  const [removal, setRemoval] = useState<{ list: ExampleList; index: number }>()

  useEffect(() => {
    if (open && example) reset(toExampleFormValues(example))
  }, [open, example, reset])

  const items: Record<ExampleList, ExampleItemValues[]> = { corrects, wrongs }
  const setList = useCallback((list: ExampleList, next: ExampleItemValues[]) => setValue(list, next, { shouldDirty: true }), [setValue])

  const handleItemSave = (item: ExampleItemValues) => {
    if (!itemEdit) return
    const current = getValues(itemEdit.list)
    setList(itemEdit.list, itemEdit.index === undefined ? [...current, item] : current.map((candidate, index) => (index === itemEdit.index ? item : candidate)))
    setItemEdit(undefined)
  }

  const handleDone = () => {
    if (example) onDone(toExampleSaveBody(example, getValues()))
  }

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-analysts:categories_example_title', { position: positionName })}
      onClose={onClose}
      onSave={handleDone}
      saveDisabled={!example}
      saveLabel={t('internal-common:done')}
      maxWidth="lg"
    >
      <Stack spacing={3} pt={1}>
        <Controller
          name="descriptions"
          control={control}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              inputType={InputTranslatorType.WYSIWYG}
              label={t('internal-analysts:categories_example_description')}
              dialogTitle={t('internal-analysts:categories_example_description')}
              selectedLanguages={categoryLanguages}
              wysiwygConfig={exampleEditorConfig}
            />
          )}
        />
        {lists.map((list) => (
          <Stack key={list} spacing={1} component="section" aria-label={t(listLabelKeys[list])}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t(listLabelKeys[list])}</Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setItemEdit({ list, item: newExampleItemValues(list) })}
                aria-label={t('internal-analysts:categories_example_item_add_to', { list: t(listLabelKeys[list]) })}
              >
                {t('internal-analysts:categories_example_item_add')}
              </Button>
            </Stack>
            <ExampleItemsTable
              items={items[list] ?? []}
              onReorder={(next) => setList(list, next)}
              onEdit={(index) => setItemEdit({ list, index, item: getValues(list)[index] })}
              onRemove={(index) => setRemoval({ list, index })}
            />
          </Stack>
        ))}
      </Stack>
      <ExampleItemDialog open={!!itemEdit} item={itemEdit?.item} onClose={() => setItemEdit(undefined)} onSave={handleItemSave} />
      <ConfirmDialog
        open={!!removal}
        confirmDialogData={{
          title: t('internal-analysts:categories_example_item_remove'),
          confirmText: t('internal-analysts:categories_example_item_remove_confirm'),
          actionText: t('internal-analysts:categories_remove'),
          destructiveAction: true,
          data: removeAction,
        }}
        onClose={(confirmed) => {
          if (confirmed?.data === removeAction && removal)
            setList(
              removal.list,
              getValues(removal.list).filter((_, index) => index !== removal.index)
            )
          setRemoval(undefined)
        }}
      />
    </InternalFormDialog>
  )
}
